"use client";

import { useEffect, useState } from "react";
import DecisionScreen from "@/components/DecisionScreen";
import BacklogScreen from "@/components/review/BacklogScreen";
import GoalsScreen from "@/components/GoalsScreen";
import BalanceReviewScreen from "@/components/review/BalanceReviewScreen";

type ReviewView = "decisions" | "library" | "goals" | "balance";

interface Props {
  onNavigate: (tab: string, initialMessage?: string) => void;
  initialView?: ReviewView;
  /** Lift subtab changes up to the route container so URL params and the
   *  OverlayManager screenContext (which drives chat grounding) stay in
   *  sync with the visible pill. Without this, clicking the pill only
   *  updates local state and chat keeps grounding against the previous
   *  subtab — see WIP-1 § 8-7 follow-up. */
  onViewChange?: (view: ReviewView) => void;
}

export default function DecideScreen({ onNavigate, initialView = "decisions", onViewChange }: Props) {
  const [subTab, setSubTab] = useState<ReviewView>(initialView);

  useEffect(() => {
    setSubTab(initialView);
  }, [initialView]);

  const handleSubTab = (view: ReviewView) => {
    setSubTab(view);
    onViewChange?.(view);
  };

  return (
    <div className="min-w-0 overflow-x-hidden px-4 pt-4 pb-20">
      <div className="mb-3">
        <h1 className="text-h1 font-semibold text-forest">Decide</h1>
        <p className="mt-1 text-sm text-muted">
          Sort what deserves action, what needs a decision, and what needs a cleaner goal or balance review.
        </p>
      </div>

      {/* Sub-tab pills */}
      <div className="mb-5 flex flex-wrap gap-1.5">
        {[
          { id: "decisions" as const, label: "Decisions" },
          { id: "library" as const, label: "Library" },
          { id: "goals" as const, label: "Goals" },
          { id: "balance" as const, label: "Balance" },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`review-subtab-${tab.id}`}
            onClick={() => handleSubTab(tab.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-150
              ${subTab === tab.id
                ? "bg-forest-med text-white"
                : "bg-white text-text-light border border-border hover:border-forest/30"
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {subTab === "decisions" && <DecisionScreen onNavigate={onNavigate} />}
      {subTab === "library" && <BacklogScreen onNavigate={onNavigate} />}
      {subTab === "goals" && <GoalsScreen onNavigate={onNavigate} />}
      {subTab === "balance" && <BalanceReviewScreen onNavigate={onNavigate} />}
    </div>
  );
}
